import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ELEMENT_TYPES, mmToPx, pxToMm } from '../../utils/constants';

export default function LayoutElement({
  element,
  zone,
  isSelected,
  previewMode,
  contentWidthMm,
  zoneHeightMm,
  displayValue,
  previewData,
  entities,
  pageInfo,
  gridSettings,
  onSelect,
  onMove,
  onResize,
  onDelete
}) {
  const elementRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const [startMouse, setStartMouse] = useState({ x: 0, y: 0 });

  // Snap to grid helper
  const snapToGrid = (value) => {
    if (!gridSettings?.snapEnabled || !gridSettings?.size) return value;
    return Math.round(value / gridSettings.size) * gridSettings.size;
  };

  // Calculate pixel positions
  const isTable = element.type === ELEMENT_TYPES.TABLE;
  // For tables on subsequent pages, position at top of zone
  const isSubsequentPage = previewMode && pageInfo && pageInfo.pageIndex > 0;
  const adjustedY = (isTable && isSubsequentPage) ? 0 : element.y;

  const x = mmToPx(element.x || 0);
  const y = mmToPx(adjustedY || 0);
  const width = mmToPx(element.width || 50);
  // Tables use auto height, so height in pixels is only for non-tables
  const height = isTable ? 'auto' : mmToPx(typeof element.height === 'number' ? element.height : 10);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    if (previewMode) return;
    e.preventDefault();

    setIsDragging(true);
    setStartPos({ x: element.x, y: element.y });
    setStartMouse({ x: e.clientX, y: e.clientY });
  };

  // Handle resize mouse down
  const handleResizeMouseDown = (e, handle) => {
    if (previewMode) return;
    e.preventDefault();
    e.stopPropagation();

    setIsResizing(true);
    setResizeHandle(handle);
    setStartSize({ width: element.width, height: element.height });
    setStartPos({ x: element.x, y: element.y });
    setStartMouse({ x: e.clientX, y: e.clientY });
  };

  // Handle mouse move
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const dx = pxToMm(e.clientX - startMouse.x);
        const dy = pxToMm(e.clientY - startMouse.y);

        let newX = startPos.x + dx;
        let newY = startPos.y + dy;

        // Apply snap to grid
        newX = snapToGrid(newX);
        newY = snapToGrid(newY);

        // Constrain to zone bounds
        newX = Math.max(0, Math.min(newX, contentWidthMm - element.width));
        newY = Math.max(0, Math.min(newY, zoneHeightMm - element.height));

        onMove(newX, newY);
      }

      if (isResizing) {
        const dx = pxToMm(e.clientX - startMouse.x);
        const dy = pxToMm(e.clientY - startMouse.y);

        let newWidth = startSize.width;
        let newHeight = startSize.height;
        let newX = startPos.x;
        let newY = startPos.y;

        // Calculate new size based on handle
        if (resizeHandle.includes('e')) {
          newWidth = Math.max(10, startSize.width + dx);
        }
        if (resizeHandle.includes('w')) {
          const widthChange = Math.min(dx, startSize.width - 10);
          newWidth = startSize.width - widthChange;
          newX = startPos.x + widthChange;
        }
        if (resizeHandle.includes('s')) {
          newHeight = Math.max(5, startSize.height + dy);
        }
        if (resizeHandle.includes('n')) {
          const heightChange = Math.min(dy, startSize.height - 5);
          newHeight = startSize.height - heightChange;
          newY = startPos.y + heightChange;
        }

        // Apply snap to grid
        newX = snapToGrid(newX);
        newY = snapToGrid(newY);
        newWidth = snapToGrid(newWidth);
        newHeight = snapToGrid(newHeight);

        // Ensure minimum sizes after snap
        newWidth = Math.max(gridSettings?.size || 5, newWidth);
        newHeight = Math.max(gridSettings?.size || 5, newHeight);

        // Constrain to zone bounds
        newX = Math.max(0, newX);
        newY = Math.max(0, newY);
        newWidth = Math.min(newWidth, contentWidthMm - newX);
        newHeight = Math.min(newHeight, zoneHeightMm - newY);

        if (newX !== element.x || newY !== element.y) {
          onMove(newX, newY);
        }
        if (newWidth !== element.width || newHeight !== element.height) {
          onResize(newWidth, newHeight);
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
    };

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, startPos, startSize, startMouse, resizeHandle, element, contentWidthMm, zoneHeightMm, gridSettings, onMove, onResize]);

  // Handle keyboard delete
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isSelected && (e.key === 'Delete' || e.key === 'Backspace')) {
        // Only delete if not editing text
        if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          onDelete();
        }
      }
    };

    if (isSelected) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSelected, onDelete]);

  // Build element style
  const elementStyle = {
    position: 'absolute',
    left: x,
    top: y,
    width: width,
    // Tables always use auto height
    height: isTable ? 'auto' : height,
    fontFamily: element.style?.family || 'Arial',
    fontSize: mmToPx(element.style?.size || 12) / 3, // Scale font size
    fontWeight: element.style?.weight || 'normal',
    fontStyle: element.style?.style || 'normal',
    color: element.style?.color || '#000000',
    textAlign: element.style?.textAlign || 'left',
    backgroundColor: element.style?.backgroundColor || 'transparent',
    padding: mmToPx(element.style?.padding || 0),
    cursor: previewMode ? 'default' : 'move',
    // Tables allow content to overflow, text elements support multi-line
    overflow: isTable ? 'visible' : 'hidden',
    whiteSpace: isTable ? 'normal' : (element.type === ELEMENT_TYPES.TEXT ? 'pre-wrap' : 'nowrap'),
    textOverflow: isTable ? 'clip' : 'ellipsis',
    lineHeight: 1.2
  };

  // Add border if specified
  if (element.style?.borderWidth > 0 && element.style?.borderStyle !== 'none') {
    elementStyle.border = `${element.style.borderWidth}px ${element.style.borderStyle} ${element.style.borderColor}`;
  }

  // Render element content based on type
  const renderContent = () => {
    switch (element.type) {
      case ELEMENT_TYPES.TEXT:
        return <div style={{ width: '100%', height: '100%' }}>{element.content}</div>;

      case ELEMENT_TYPES.DATA_FIELD:
        return (
          <div style={{ width: '100%', height: '100%' }} className={displayValue.startsWith('{') ? 'field-placeholder' : ''}>
            {displayValue}
          </div>
        );

      case ELEMENT_TYPES.PAGE_NUMBER:
        // In preview mode with pagination, show actual page numbers
        const pageNumber = pageInfo?.pageIndex !== undefined ? pageInfo.pageIndex + 1 : 1;
        const totalPages = pageInfo?.totalPages || 1;
        const pageNumberText = previewMode
          ? element.format.replace('{page}', pageNumber).replace('{pages}', totalPages)
          : displayValue;
        return <div style={{ width: '100%', height: '100%' }}>{pageNumberText}</div>;

      case ELEMENT_TYPES.TABLE:
        return renderTable();

      case ELEMENT_TYPES.IMAGE:
        return element.src ? (
          <img
            src={element.src}
            alt=""
            style={{
              width: '100%',
              height: '100%',
              objectFit: element.style?.objectFit || 'contain'
            }}
          />
        ) : (
          <div className="image-placeholder">
            <FontAwesomeIcon icon="image" />
          </div>
        );

      case ELEMENT_TYPES.RECTANGLE:
        // Rectangle is just a styled box, content is empty
        return null;

      default:
        return null;
    }
  };

  // Render table element
  const renderTable = () => {
    const columns = element.columns || [];

    // Get data for this table's query
    const getTableData = () => {
      if (!previewData) return [];
      // Check if previewData is the new format (object with queryIds)
      if (typeof previewData === 'object' && !Array.isArray(previewData)) {
        const queryId = element.queryId;
        if (queryId && previewData[queryId]?.data) {
          return previewData[queryId].data;
        }
        // Try to find data from column queryId
        for (const col of columns) {
          const colQueryId = col.queryId || col.field?.queryId;
          if (colQueryId && previewData[colQueryId]?.data) {
            return previewData[colQueryId].data;
          }
        }
        return [];
      }
      // Legacy: previewData is array
      return Array.isArray(previewData) ? previewData : [];
    };

    const tableData = getTableData();
    // In preview mode with pagination, slice rows according to pageInfo
    // In editor mode show only 5 as sample
    const rows = previewMode
      ? (pageInfo && pageInfo.startRow !== undefined && pageInfo.endRow !== undefined
          ? tableData.slice(pageInfo.startRow, pageInfo.endRow)
          : tableData)
      : tableData.slice(0, 5);

    // Empty table placeholder
    if (columns.length === 0) {
      return (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
          border: '2px dashed #dee2e6',
          borderRadius: '4px',
          color: '#6c757d',
          fontSize: '11px',
          textAlign: 'center',
          padding: '10px'
        }}>
          <FontAwesomeIcon icon="table" style={{ fontSize: '20px', marginBottom: '6px', opacity: 0.5 }} />
          <span>Tabella vuota</span>
          <span style={{ fontSize: '10px', marginTop: '4px' }}>Seleziona e configura le colonne dal pannello Stile</span>
        </div>
      );
    }

    // Get column header text
    const getColumnHeader = (col) => {
      // Use displayLabel for externalEntity with subField
      if (col.field?.displayLabel) return col.field.displayLabel;
      if (col.field?.label) return col.field.label;
      if (col.headerText) return col.headerText;
      return 'Colonna';
    };

    // Get column cell value
    const getCellValue = (col, row) => {
      // Get the raw field value first
      let rawValue = '';

      if (col.field?.name && row) {
        const fieldValue = row[col.field.name];

        // Handle externalEntity fields with subField
        if (col.field.subField && fieldValue && typeof fieldValue === 'object') {
          const subValue = fieldValue[col.field.subField];
          rawValue = subValue !== undefined ? String(subValue) : '';
        }
        // Handle externalEntity fields without explicit subField
        else if (['externalEntity', 'deepExternalEntity'].includes(col.field.type) && fieldValue && typeof fieldValue === 'object') {
          if (col.field.options?.labelField && fieldValue[col.field.options.labelField] !== undefined) {
            rawValue = String(fieldValue[col.field.options.labelField]);
          } else {
            for (const key of ['name', 'title', 'label', 'description', 'id']) {
              if (fieldValue[key] !== undefined) {
                rawValue = String(fieldValue[key]);
                break;
              }
            }
            if (!rawValue) {
              const firstValue = Object.values(fieldValue).find(v => typeof v === 'string' || typeof v === 'number');
              rawValue = firstValue !== undefined ? String(firstValue) : '[oggetto]';
            }
          }
        }
        // Regular field
        else if (fieldValue !== undefined) {
          rawValue = String(fieldValue);
        }
      }

      // If there's a cell template, use it
      if (col.cellTemplate) {
        // Replace {valore} with the raw field value
        return col.cellTemplate.replace(/\{valore\}/gi, rawValue);
      }

      // No template - return raw value or empty
      return rawValue;
    };

    // Get text alignment for table (from element style)
    const tableTextAlign = element.style?.textAlign || 'left';
    const showHeader = element.style?.showHeader !== false;

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'inherit' }}>
        {showHeader && (
          <thead>
            <tr>
              {columns.map((col, i) => (
                <th
                  key={col.id || i}
                  style={{
                    padding: '4px 8px',
                    background: element.style?.headerBackground || '#f8f9fa',
                    borderBottom: `${element.style?.borderWidth || 1}px solid ${element.style?.borderColor || '#ddd'}`,
                    textAlign: col.align || tableTextAlign,
                    fontWeight: 'bold',
                    width: col.width !== 'auto' ? col.width : undefined
                  }}
                >
                  {getColumnHeader(col)}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col, colIndex) => (
                  <td
                    key={col.id || colIndex}
                    style={{
                      padding: '4px 8px',
                      background: rowIndex % 2 === 0
                        ? element.style?.rowBackground || '#fff'
                        : element.style?.alternateRowBackground || '#f8f9fa',
                      borderBottom: `${element.style?.borderWidth || 1}px solid ${element.style?.borderColor || '#ddd'}`,
                      textAlign: col.align || tableTextAlign,
                      width: col.width !== 'auto' ? col.width : undefined
                    }}
                  >
                    {getCellValue(col, row)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              {columns.map((col, i) => {
                // Get placeholder text
                let placeholder = '';
                if (col.cellTemplate) {
                  // Show template with field placeholder
                  const fieldPlaceholder = col.field
                    ? `{${col.field.displayLabel || col.field.label}}`
                    : '';
                  placeholder = col.cellTemplate.replace(/\{valore\}/gi, fieldPlaceholder);
                } else if (col.field) {
                  placeholder = col.field.displayLabel
                    ? `{${col.field.displayLabel}}`
                    : `{${col.field.label}}`;
                } else {
                  placeholder = col.headerText || 'testo';
                }
                return (
                  <td
                    key={col.id || i}
                    style={{
                      padding: '4px 8px',
                      color: '#999',
                      fontStyle: 'italic',
                      textAlign: col.align || tableTextAlign,
                      width: col.width !== 'auto' ? col.width : undefined
                    }}
                  >
                    {placeholder}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  // Get element class name
  const getElementClassName = () => {
    let className = `layout-element element-${element.type}`;
    if (isSelected) className += ' selected';
    if (isDragging) className += ' dragging';
    if (isResizing) className += ' resizing';
    return className;
  };

  return (
    <div
      ref={elementRef}
      className={getElementClassName()}
      style={elementStyle}
      onClick={onSelect}
      onMouseDown={handleMouseDown}
    >
      {renderContent()}

      {/* Resize handles */}
      {isSelected && !previewMode && (
        <>
          <div className="resize-handle nw" onMouseDown={(e) => handleResizeMouseDown(e, 'nw')} />
          <div className="resize-handle n" onMouseDown={(e) => handleResizeMouseDown(e, 'n')} />
          <div className="resize-handle ne" onMouseDown={(e) => handleResizeMouseDown(e, 'ne')} />
          <div className="resize-handle e" onMouseDown={(e) => handleResizeMouseDown(e, 'e')} />
          <div className="resize-handle se" onMouseDown={(e) => handleResizeMouseDown(e, 'se')} />
          <div className="resize-handle s" onMouseDown={(e) => handleResizeMouseDown(e, 's')} />
          <div className="resize-handle sw" onMouseDown={(e) => handleResizeMouseDown(e, 'sw')} />
          <div className="resize-handle w" onMouseDown={(e) => handleResizeMouseDown(e, 'w')} />
        </>
      )}
    </div>
  );
}
